import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ADMIN_TOKEN_KEY } from "@/lib/api";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Overview" },
  { to: "/map", label: "Map" },
  { to: "/browse/hotels", label: "Hotels" },
  { to: "/browse/banquets", label: "Banquets" },
  { to: "/browse/rooms", label: "Rooms" },
  { to: "/browse/restaurants", label: "Restaurants" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isAdmin = typeof window !== "undefined" && !!localStorage.getItem(ADMIN_TOKEN_KEY);

  const linkClass = ({ isActive }) =>
    `font-label text-xs px-2 py-1 border-b-2 whitespace-nowrap ${
      isActive
        ? "border-[var(--brand-blue)] text-[var(--ink)]"
        : "border-transparent text-[var(--ink-soft)] hover:text-[var(--ink)]"
    }`;

  return (
    <header
      data-testid="site-nav"
      className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[var(--line)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-home-link" className="flex items-center gap-3 shrink-0">
          <div className="w-8 h-8 bg-[var(--brand-blue)] flex items-center justify-center">
            <span className="text-white font-display text-lg leading-none">R</span>
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-lg tracking-tight">ROYAL ORCHID</span>
            <span className="font-label text-[10px] text-[var(--ink-soft)]">Presence Deck</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-3 xl:gap-5">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`nav-${l.label.toLowerCase()}`} className={linkClass}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {isAdmin ? (
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className="bg-[var(--brand-blue)] text-white px-3 sm:px-4 py-2 font-display text-xs sm:text-sm hover:bg-black transition-colors"
            >
              ADMIN
            </Link>
          ) : (
            <Link
              to="/admin/login"
              data-testid="nav-admin-login-link"
              className="border-2 border-[var(--ink)] text-[var(--ink)] px-3 sm:px-4 py-2 font-display text-xs sm:text-sm hover:bg-[var(--ink)] hover:text-white transition-colors"
            >
              ADMIN LOGIN
            </Link>
          )}
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 border-2 border-[var(--ink)]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden border-t border-[var(--line)] bg-white"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 grid grid-cols-2 gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-display text-sm p-3 border ${
                    isActive
                      ? "border-[var(--brand-blue)] text-[var(--brand-blue)]"
                      : "border-[var(--line)] text-[var(--ink)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
